import {
  countryDisplayName,
  formatAmountWithUnit,
  type PublicShippingDestination,
  SHIPPING_ADDRESS_FIELD_MAX,
  SHIPPING_ADDRESS_FIELDS,
  type ShippingAddress,
  type ShippingAddressValidation,
  US_STATE_OPTIONS,
} from "@loot-os/api-contract";
import { Alert } from "@loot-os/design-system/core/Alert";
import { Button } from "@loot-os/design-system/core/Button";
import { Input } from "@loot-os/design-system/core/Input";
import { Modal, ModalClose, ModalFooter } from "@loot-os/design-system/core/Modal";
import { Select } from "@loot-os/design-system/core/Select";
import { useId, useState } from "react";

import { useCurrency } from "../lib/currency.js";
import {
  type AddressDraft,
  addressLines,
  changedFields,
  draftIssue,
  draftToAddress,
  regionMode,
  shippingErrorMessage,
  toDraft,
  useSaveShippingAddress,
  useValidateShippingAddress,
} from "../lib/shippingAddress.js";
import { useTerminology } from "../lib/terminology.js";

const US_STATE_SELECT = US_STATE_OPTIONS.map((s) => ({ value: s.code, label: s.name }));

/** The rows the form lays out, in boxed's order: two names, the street, country and region, city and postal code, phone. */
const ROWS: (keyof ShippingAddress)[][] = [
  ["firstName", "lastName"],
  ["addressLine1"],
  ["addressLine2"],
  ["country", "region"],
  ["locality", "postalCode"],
  ["phoneNumber"],
];

const LABELS = Object.fromEntries(SHIPPING_ADDRESS_FIELDS.map((f) => [f.key, f.label])) as Record<
  keyof ShippingAddress,
  string
>;
const REQUIRED = new Set(SHIPPING_ADDRESS_FIELDS.filter((f) => f.required).map((f) => f.key));

type Step =
  | { kind: "form" }
  | { kind: "confirm"; entered: ShippingAddress; verdict: ShippingAddressValidation };

/**
 * The shopper's address form (boxed's AddressForm + ConfirmAddressModal), on the design system's
 * Modal: the country is one of the store's destinations, a US region is a state, and submitting
 * first asks address validation. A clean verdict saves at once; a suggestion is shown beside what
 * was typed to accept or keep; an address the validator rejects can still be saved, and the
 * server's own country check has the last word.
 */
export function ShippingAddressDialog({
  open,
  onOpenChange,
  address,
  destinations,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  address: ShippingAddress | null;
  destinations: readonly PublicShippingDestination[];
}) {
  const formId = useId();
  const currency = useCurrency();
  const { item } = useTerminology();
  const validate = useValidateShippingAddress();
  const save = useSaveShippingAddress();
  const [draft, setDraft] = useState<AddressDraft>(() => toDraft(address));
  const [step, setStep] = useState<Step>({ kind: "form" });
  const [error, setError] = useState<string | null>(null);

  const countryOptions = destinations.map((d) => ({
    value: d.countryCode,
    label: countryDisplayName(d.countryCode),
  }));
  const destination = destinations.find((d) => d.countryCode === draft.country);
  const candidate = draftToAddress(draft);
  const issue = draftIssue(draft);
  const busy = validate.isPending || save.isPending;
  const usState = regionMode(draft.country) === "us-state";

  const reset = (next: boolean) => {
    if (next) {
      setDraft(toDraft(address));
      setStep({ kind: "form" });
      setError(null);
    }
    onOpenChange(next);
  };

  const commit = (toSave: ShippingAddress) => {
    setError(null);
    save.mutate(toSave, {
      onSuccess: () => onOpenChange(false),
      onError: (err) => {
        setStep({ kind: "form" });
        setError(shippingErrorMessage(err));
      },
    });
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!candidate || issue) return;
    setError(null);
    validate.mutate(candidate, {
      onSuccess: (verdict) => {
        if (verdict.status === "valid" || verdict.status === "unverified") commit(candidate);
        else setStep({ kind: "confirm", entered: candidate, verdict });
      },
      onError: (err) => setError(shippingErrorMessage(err)),
    });
  };

  const field = (key: keyof ShippingAddress) => {
    if (key === "country") {
      return (
        <Select
          label={LABELS[key]}
          placeholder="Choose a country"
          options={countryOptions}
          value={draft.country || null}
          // A new country empties the region: a state code means nothing elsewhere.
          onValueChange={(value) => setDraft({ ...draft, country: value, region: "" })}
        />
      );
    }
    if (key === "region" && usState) {
      return (
        <Select
          label="State"
          placeholder="Choose a state"
          options={US_STATE_SELECT}
          value={draft.region || null}
          onValueChange={(value) => setDraft({ ...draft, region: value })}
        />
      );
    }
    return (
      <Input
        label={LABELS[key]}
        value={draft[key]}
        maxLength={SHIPPING_ADDRESS_FIELD_MAX}
        required={REQUIRED.has(key)}
        autoComplete={AUTOCOMPLETE[key]}
        onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
      />
    );
  };

  const view =
    step.kind === "confirm"
      ? confirmView({
          entered: step.entered,
          verdict: step.verdict,
          busy,
          error,
          onEdit: () => setStep({ kind: "form" }),
          onSave: commit,
        })
      : {
          title: "Shipping address",
          description: `Where your ${item.plural} ship. Everything except the second line and the phone number is required.`,
          footer: (
            <ModalFooter>
              <ModalClose render={<Button type="button" variant="ghost" />}>Cancel</ModalClose>
              <Button
                type="submit"
                form={formId}
                disabled={!candidate || issue !== null || busy}
                loading={busy}
              >
                {validate.isPending ? "Checking…" : save.isPending ? "Saving…" : "Save address"}
              </Button>
            </ModalFooter>
          ),
          body: (
            <form id={formId} className="flex flex-col gap-3" onSubmit={submit}>
              {ROWS.map((row) => (
                <div key={row.join("-")} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {row.map((key) => (
                    <div key={key} className={row.length === 1 ? "sm:col-span-2" : undefined}>
                      {field(key)}
                    </div>
                  ))}
                </div>
              ))}
              {destination && (
                <p className="font-body text-body-b3 text-text-secondary">
                  Shipping to {countryDisplayName(destination.countryCode)}:{" "}
                  {destination.costCents === 0
                    ? "free"
                    : formatAmountWithUnit(destination.costCents, currency)}{" "}
                  · {destination.deliveryEstimate}
                </p>
              )}
              {issue && <p className="font-body text-body-b3 text-status-danger">{issue}</p>}
              {error && <Alert tone="danger">{error}</Alert>}
            </form>
          ),
        };

  // One Modal for both steps, so moving to the confirmation doesn't close and reopen it.
  return (
    <Modal
      open={open}
      onOpenChange={reset}
      size="lg"
      title={view.title}
      description={view.description}
      footer={view.footer}
    >
      {view.body}
    </Modal>
  );
}

const AUTOCOMPLETE: Partial<Record<keyof ShippingAddress, string>> = {
  firstName: "given-name",
  lastName: "family-name",
  addressLine1: "address-line1",
  addressLine2: "address-line2",
  locality: "address-level2",
  region: "address-level1",
  postalCode: "postal-code",
  phoneNumber: "tel",
};

/**
 * What address validation said, and the choice it leaves: a suggestion to accept or decline, or
 * an address it could not find, which the shopper may save anyway.
 */
function confirmView({
  entered,
  verdict,
  busy,
  error,
  onEdit,
  onSave,
}: {
  entered: ShippingAddress;
  verdict: ShippingAddressValidation;
  busy: boolean;
  error: string | null;
  onEdit: () => void;
  onSave: (address: ShippingAddress) => void;
}) {
  const suggested = verdict.correctedAddress ?? null;
  const changed = suggested ? new Set(changedFields(entered, suggested)) : new Set<string>();
  const hasSuggestion = suggested !== null && changed.size > 0;
  return {
    title: hasSuggestion ? "Use the suggested address?" : "Address not found",
    description: hasSuggestion
      ? "Address validation suggests a small correction. Pick the version to save."
      : "Address validation could not confirm this address. Check it, or save it as typed.",
    footer: (
      <ModalFooter>
        <Button type="button" variant="ghost" disabled={busy} onClick={onEdit}>
          Edit
        </Button>
        <Button
          type="button"
          variant={hasSuggestion ? "secondary" : "primary"}
          disabled={busy}
          onClick={() => onSave(entered)}
        >
          {hasSuggestion ? "Keep what I entered" : busy ? "Saving…" : "Save anyway"}
        </Button>
        {hasSuggestion && suggested && (
          <Button type="button" disabled={busy} onClick={() => onSave(suggested)}>
            {busy ? "Saving…" : "Use suggested"}
          </Button>
        )}
      </ModalFooter>
    ),
    body: (
      <>
        <div className={`grid grid-cols-1 gap-4 ${hasSuggestion ? "sm:grid-cols-2" : ""}`}>
          <AddressBlock title="You entered" address={entered} />
          {hasSuggestion && suggested && (
            <AddressBlock title="Suggested" address={suggested} highlight={changed} />
          )}
        </div>
        {error && <Alert tone="danger">{error}</Alert>}
      </>
    ),
  };
}

function AddressBlock({
  title,
  address,
  highlight,
}: {
  title: string;
  address: ShippingAddress;
  highlight?: ReadonlySet<string>;
}) {
  // Which line carries which field, so a changed field can be marked.
  const lines: [string, string][] = [
    ["name", `${address.firstName} ${address.lastName}`],
    ["addressLine1", address.addressLine1],
    ...(address.addressLine2
      ? ([["addressLine2", address.addressLine2]] as [string, string][])
      : []),
    ["locality", `${address.locality}, ${address.region} ${address.postalCode}`],
    ["country", countryDisplayName(address.country)],
  ];
  const changedLine = (key: string) =>
    key === "locality"
      ? ["locality", "region", "postalCode"].some((k) => highlight?.has(k))
      : highlight?.has(key);
  return (
    <div className="rounded-xl border border-border-subtle bg-bg-inset p-3 font-body text-body-b2 text-text-primary">
      <p className="mb-1 text-body-b3-strong text-text-secondary">{title}</p>
      <address className="flex flex-col gap-0.5 not-italic">
        {lines.map(([key, text]) => (
          <span
            key={key}
            className={changedLine(key) ? "text-body-b2-strong text-accent-text" : undefined}
          >
            {text}
          </span>
        ))}
      </address>
      {addressLines(address).length > lines.length && address.phoneNumber && (
        <p className="mt-1 text-body-b3 text-text-secondary">{address.phoneNumber}</p>
      )}
    </div>
  );
}
